import { ModelToRun } from '@/scenes/engine/common/constants'
import {
  buildDefaultRequirements,
  GRID_TOP_LEVEL_REQUIREMENTS,
  GRID_VARIANT_EXTRA_REQUIREMENTS,
  HI_RES_COMPOSITION_PROMPT,
  isMinorSubjectAge,
  isMannequinSubjectGender,
} from '@/scenes/engine/utils/aiFashionConstants'
import { buildStructuredPrompt, ImageIndices } from '@/scenes/engine/utils/aiFashionPromptBuilder'
import {
  AIFashionSession,
  PoseTile,
} from '@/scenes/engine/utils/aiFashionSession'
import {
  getPoseSignatureFromSession,
  poseCacheSignatureKey,
  poseCacheSignaturesEqual,
  PoseCacheSignature,
} from '@/scenes/engine/utils/aiFashionPoseCache'
import type {
  AIModelsAccessDecision,
} from '@/services/ai/aiAccessPolicy'
import type {
  AIModelsPoseComposition,
  AIModelsPromptMetadata,
} from '@/store/slices/aiFashionModels/types'
import type { ImageQuality } from '@/store/slices/aiFashion/types'
import type { GenerateImageAnalyticsOptions, GenerateImageSafetyOptions } from '@/services/ai/imageService'
import { loadImageFromURL } from '@/scenes/engine/utils/image-loader'
import { centerCropToAspectRatio } from '@/utils/centerCropToAspectRatio'

export const POSE_GRID_DIMENSION = 2
export const POSE_GRID_DIMENSION_COLUMN = 3
/** Batch AI Studio On model is a 3×3 picker; Editor AI Fashion stays at `POSE_GRID_DIMENSION`. */
export const BATCH_ON_MODEL_POSE_GRID_DIMENSION = 3
export const AI_MODELS_ACCESS_FRESH_TTL_MS = 10000
/** Inner-padding inset (px) applied when slicing the AI Models pose grid to avoid
 * seam bleed between abutted panels. Mirrors iOS `AIGridImageSlicer` 1.5pt inset. */
export const AI_MODELS_GRID_INNER_PADDING_PX = 2
/** Ratio slack a generated result may keep before it is center-cropped. */
const AI_FASHION_CROP_TOLERANCE = 0.02

export interface AIFashionServiceApi {
  getAIModelsPoseCompositions(
    referenceImageBase64: string,
    variants?: number,
    product?: string,
    abortSignal?: AbortSignal
  ): Promise<AIModelsPoseComposition[]>
  getAIModelsPromptMetadata(
    productImageBase64: string,
    abortSignal?: AbortSignal
  ): Promise<AIModelsPromptMetadata>
}

export interface AIFashionServiceImageProcessing {
  resizeBase64ToMaxEdgeSizeAsJpeg(image: string, edge: number, quality?: number): Promise<string>
  resizeBase64ToMaxEdgeSize(image: string, edge: number): Promise<string>
}

export interface GenerateImageResult {
  success: boolean
  image?: string | null
}

export type GenerateImageFn = (
  imageData: string[],
  prompt: string,
  provider: ModelToRun,
  imageMask: string | null,
  aspectRatio?: number,
  quality?: number | null,
  abortSignal?: AbortSignal,
  analyticsOptions?: GenerateImageAnalyticsOptions,
  resolution?: ImageQuality,
  safetyOptions?: GenerateImageSafetyOptions
) => Promise<GenerateImageResult>

export type SliceVariantGridFn = (
  gridImage: string,
  rows: number,
  columns: number,
  innerPaddingPx?: number
) => Promise<string[]>

export interface AIFashionServiceDeps {
  api: AIFashionServiceApi
  imageProcessing: AIFashionServiceImageProcessing
  generateImage: GenerateImageFn
  sliceVariantGrid: SliceVariantGridFn
  getAccessDecision: () => AIModelsAccessDecision
  ensureAIModelsCreditsFresh: (ttlMs: number) => Promise<boolean>
  fetchModelReferenceImage?: (imageUrl: string, abortSignal?: AbortSignal) => Promise<string | null>
  loadImageFromURL?: typeof loadImageFromURL
  /**
   * Max-edge cap (px) applied to generated results.
   * - `number`: resize the result so its longest edge does not exceed this value.
   * - `null`: no resize — keep the model's native resolution.
   */
  resultMaxEdge: number | null
}

export interface FetchPoseTilesOptions {
  session: AIFashionSession
  resolveProductBase64: (maxEdge: number) => Promise<string | null>
  aspectRatio: number
  onUpdate: (tiles: PoseTile[], loading: boolean) => void
}

export interface FetchMorePoseTilesOptions {
  session: AIFashionSession
  resolveProductBase64: (maxEdge: number) => Promise<string | null>
  aspectRatio: number
  onUpdate: (tiles: PoseTile[], loadingMore: boolean) => void
}

export interface GenerateOptions {
  session: AIFashionSession
  productBase64: string
  aspectRatio: number
  poseSelectedTileImage: string | null
  abortSignal?: AbortSignal
}

export interface GenerationResult {
  success: boolean
  image: string | null
  structuredPrompt: string | null
  snapshotPoseSelectedTileKey: string | null
  aborted: boolean
}

class AIFashionService {
  private cachedModelRefImage: { url: string; base64: string } | null = null
  private abortController: AbortController | null = null
  private tilesBySignature = new Map<string, PoseTile[]>()
  private compositionsByProduct = new Map<string, AIModelsPoseComposition[]>()
  private poseLoading = false
  private poseLoadingMore = false
  private poseFailCount = 0
  private currentPoseSignature: PoseCacheSignature | null = null
  private poseTileEpoch = 0

  constructor(private readonly deps: AIFashionServiceDeps) {}

  reset(): void {
    this.cachedModelRefImage = null
    this.tilesBySignature.clear()
    this.compositionsByProduct.clear()
    this.poseLoading = false
    this.poseLoadingMore = false
    this.poseFailCount = 0
    this.currentPoseSignature = null
    this.poseTileEpoch = 0
    this.cancelGeneration()
  }

  clearPoseCaches(): void {
    this.tilesBySignature.clear()
    this.compositionsByProduct.clear()
    this.poseLoading = false
    this.poseLoadingMore = false
    this.poseFailCount = 0
    this.currentPoseSignature = null
    this.poseTileEpoch = 0
  }

  beginGeneration(): AbortSignal {
    this.cancelGeneration()
    this.abortController = new AbortController()
    return this.abortController.signal
  }

  cancelGeneration(): void {
    this.abortController?.abort()
    this.abortController = null
  }

  releaseGeneration(signal: AbortSignal): void {
    if (this.abortController?.signal === signal) {
      this.abortController = null
    }
  }

  isCurrentGeneration(signal: AbortSignal): boolean {
    return this.abortController?.signal === signal
  }

  getCachedPoseTiles(session: AIFashionSession): PoseTile[] {
    const sig = this.getCurrentPoseSignature(session)
    this.currentPoseSignature = sig
    this.poseLoading = false
    this.poseLoadingMore = false
    this.poseFailCount = 0
    this.poseTileEpoch += 1
    return this.tilesBySignature.get(poseCacheSignatureKey(sig)) ?? []
  }

  invalidatePoseTiles(session: AIFashionSession): PoseTile[] {
    const newSig = this.getCurrentPoseSignature(session)
    if (poseCacheSignaturesEqual(this.currentPoseSignature, newSig)) {
      return this.tilesBySignature.get(poseCacheSignatureKey(newSig)) ?? []
    }
    this.currentPoseSignature = newSig
    this.poseLoading = false
    this.poseLoadingMore = false
    this.poseFailCount = 0
    this.poseTileEpoch += 1
    return this.tilesBySignature.get(poseCacheSignatureKey(newSig)) ?? []
  }

  fetchPoseTiles(options: FetchPoseTilesOptions): void {
    if (this.poseLoading) return
    if (this.poseFailCount >= 2) return

    const sig = this.getCurrentPoseSignature(options.session)
    this.currentPoseSignature = sig
    const cached = this.tilesBySignature.get(poseCacheSignatureKey(sig))
    if (cached && cached.length > 0) {
      options.onUpdate(cached, false)
      return
    }

    this.poseLoading = true
    options.onUpdate([], true)

    const epochAtStart = this.poseTileEpoch
    ;(async () => {
      try {
        const accessDecision = await this.getFreshAccessDecision()
        if (!accessDecision.allowed) {
          this.poseLoading = false
          if (this.poseTileEpoch === epochAtStart) options.onUpdate([], false)
          return
        }

        const productBase64 = await options.resolveProductBase64(256)
        if (!productBase64) {
          this.poseLoading = false
          if (this.poseTileEpoch === epochAtStart) options.onUpdate([], false)
          return
        }

        const resolvedSig = this.getCurrentPoseSignature(options.session)
        const productId = resolvedSig.productId ?? ''
        let compositions = this.compositionsByProduct.get(productId)
        if (!compositions || compositions.length === 0) {
          const productDescription = options.session.promptMetadata?.product
          compositions = await this.deps.api.getAIModelsPoseCompositions(productBase64, 4, productDescription)
          this.compositionsByProduct.set(productId, compositions)
        }

        const tiles = await this.generatePoseTiles(options.session, compositions, productBase64, options.aspectRatio)
        this.tilesBySignature.set(poseCacheSignatureKey(resolvedSig), tiles)
        this.poseLoading = false
        if (this.poseTileEpoch !== epochAtStart) return
        this.currentPoseSignature = resolvedSig
        this.poseFailCount = 0
        options.onUpdate(tiles, false)
      } catch (err) {
        this.poseLoading = false
        if (this.poseTileEpoch !== epochAtStart) return
        this.poseFailCount += 1
        console.error('Failed to load pose tiles:', err)
        options.onUpdate([], false)
      }
    })()
  }

  fetchMorePoseTiles(options: FetchMorePoseTilesOptions): void {
    if (this.poseLoadingMore) return

    const sig = this.getCurrentPoseSignature(options.session)
    const currentTiles = this.tilesBySignature.get(poseCacheSignatureKey(sig)) ?? []
    this.poseLoadingMore = true
    options.onUpdate(currentTiles, true)

    const epochAtStart = this.poseTileEpoch
    ;(async () => {
      try {
        const accessDecision = await this.getFreshAccessDecision()
        if (!accessDecision.allowed) {
          this.poseLoadingMore = false
          if (this.poseTileEpoch === epochAtStart) options.onUpdate(currentTiles, false)
          return
        }

        const productBase64 = await options.resolveProductBase64(256)
        if (!productBase64) {
          this.poseLoadingMore = false
          if (this.poseTileEpoch === epochAtStart) options.onUpdate(currentTiles, false)
          return
        }

        const resolvedSig = this.getCurrentPoseSignature(options.session)
        const resolvedCurrentTiles = this.tilesBySignature.get(poseCacheSignatureKey(resolvedSig)) ?? currentTiles
        const productDescription = options.session.promptMetadata?.product
        const compositions = await this.deps.api.getAIModelsPoseCompositions(productBase64, 4, productDescription)
        if (this.poseTileEpoch !== epochAtStart) {
          this.poseLoadingMore = false
          return
        }

        const newTiles = await this.generatePoseTiles(options.session, compositions, productBase64, options.aspectRatio)
        const allTiles = [...resolvedCurrentTiles, ...newTiles]
        this.tilesBySignature.set(poseCacheSignatureKey(resolvedSig), allTiles)
        this.poseLoadingMore = false
        if (this.poseTileEpoch !== epochAtStart) return
        options.onUpdate(allTiles, false)
      } catch (err) {
        this.poseLoadingMore = false
        if (this.poseTileEpoch !== epochAtStart) return
        console.error('Failed to load more pose tiles:', err)
        options.onUpdate(currentTiles, false)
      }
    })()
  }

  async buildBasePromptForPoseGrid(session: AIFashionSession): Promise<Record<string, any> | null> {
    if (!session.promptMetadata) return null
    try {
      const background = session.backgroundPromptOverride ?? 'studio background'
      const hasModel = !!session.selectedTemplate?.imageUrl
      const imageIndices: ImageIndices = {
        productImageCount: 1,
        productImageStartIndex: 1,
        modelImageIndex: hasModel ? 2 : null,
      }
      const promptStr = buildStructuredPrompt(session.promptMetadata, {
        poseOverride: session.poseOverride,
        backgroundPromptOverride: background,
        bodyType: session.selectedTemplate?.prompt || null,
        age: session.selectedTemplate?.age ?? null,
        isWearable: session.promptMetadata.isWearable ?? null,
        isMannequinSubject: isMannequinSubjectGender(session.selectedTemplate?.gender),
      }, imageIndices)
      return JSON.parse(promptStr)
    } catch {
      return null
    }
  }

  /**
   * Extracts the fields shared across grid variant entries from the base
   * structured prompt. Port of iOS `AIModelsPoseManager.sharedVariantFields`.
   */
  private extractSharedVariantFields(base: Record<string, any> | null): Record<string, any> {
    if (!base) return {}
    const fields: Record<string, any> = {}
    if (base.product !== undefined) fields.product = base.product
    if (base.instruction !== undefined) fields.instruction = base.instruction
    if (base.subject !== undefined) fields.subject = base.subject
    else if (base.body_type !== undefined) fields.body_type = base.body_type
    if (base.background !== undefined) fields.background = base.background
    return fields
  }

  async generatePoseGrid(
    session: AIFashionSession,
    compositions: AIModelsPoseComposition[],
    productBase64: string,
    aspectRatio: number,
    abortSignal?: AbortSignal,
    gridDimension: number = POSE_GRID_DIMENSION,
  ): Promise<Array<{ image: string; composition: AIModelsPoseComposition }>> {
    const variantCount = gridDimension * gridDimension

    if (compositions.length === 0) {
      throw new Error('No compositions provided for pose grid generation')
    }

    const slicedCompositions = compositions.slice(0, variantCount)

    const basePromptJson = await this.buildBasePromptForPoseGrid(session)
    const sharedFields = this.extractSharedVariantFields(basePromptJson)
    const subjectAge = typeof basePromptJson?.subject === 'object' && basePromptJson.subject !== null
      ? (basePromptJson.subject.age as string | undefined)
      : undefined
    // iOS parity: AIModelsPoseManager.buildGridPrompt threads isWearable into
    // defaultRequirements so non-wearable products (bags, bottles, etc.) don't get
    // "must be worn" phrasing in the grid variant requirements.
    const isWearable = session.promptMetadata?.isWearable ?? true
    const variantRequirements = [
      ...buildDefaultRequirements({
        isMinorSubject: isMinorSubjectAge(subjectAge),
        isWearable,
        isMannequinSubject: isMannequinSubjectGender(session.selectedTemplate?.gender),
      }),
      ...GRID_VARIANT_EXTRA_REQUIREMENTS,
    ]

    const variants = slicedCompositions.map((comp, i) => ({
      index: i + 1,
      requirements: variantRequirements,
      composition: { pose: comp.pose, camera_angle: comp.camera_angle, crop: comp.crop },
      ...sharedFields,
    }))

    const gridPrompt = JSON.stringify({
      task: `Generate a single, seamless ${gridDimension}x${gridDimension} grid image with ${variantCount} flush-edge panels. Treat the entire output as one canvas with zero guttering.`,
      layout_constraint: `Create a flush-edge, wall-to-wall ${gridDimension}x${gridDimension} grid. Strictly NO white space, NO grid lines, NO borders, and NO margins between images. Every panel must be tightly abutted and 'bleed' directly into the next, creating a continuous, seamless composition. There must be zero pixels of background visible between the images. Strictly zero spacing. No white borders, no gutters, and no padding between panels. Images must touch edge-to-edge.`,
      requirements: GRID_TOP_LEVEL_REQUIREMENTS,
      variants,
    })

    const modelImageBase64 = session.selectedTemplate?.imageUrl
      ? await this.resolveModelReferenceImage(session.selectedTemplate.imageUrl, abortSignal)
      : null

    if (session.selectedTemplate?.imageUrl && !modelImageBase64) {
      throw new Error('Failed to load model reference image for pose grid')
    }

    const attachments = modelImageBase64
      ? [productBase64, modelImageBase64]
      : [productBase64]

    const { success, image: gridImage } = await this.deps.generateImage(
      attachments,
      gridPrompt,
      ModelToRun.GEMINI_3_1,
      null,
      aspectRatio,
      null,
      abortSignal,
      { type: 'AI_models', prompt: 'Pose grid generation' },
      undefined,
      // iOS parity: skip prompt-only safety when prompt text is app-generated base/template JSON.
      { skipPromptSafety: true }
    )

    if (!success || !gridImage) {
      throw new Error('Failed to generate pose grid image')
    }

    const tiles = await this.deps.sliceVariantGrid(gridImage, gridDimension, gridDimension, AI_MODELS_GRID_INNER_PADDING_PX)
    return tiles
      .map((tile, i) => ({ image: tile, composition: slicedCompositions[i] }))
      .filter(item => item.composition != null)
  }

  async getFreshAccessDecision(): Promise<AIModelsAccessDecision> {
    const decision = this.deps.getAccessDecision()
    if (decision.reason === 'premium') return decision

    const isFresh = await this.deps.ensureAIModelsCreditsFresh(AI_MODELS_ACCESS_FRESH_TTL_MS)
    if (!isFresh) return { ...decision, allowed: false, reason: 'noAccess' }

    return this.deps.getAccessDecision()
  }

  async generate(options: GenerateOptions): Promise<GenerationResult> {
    const { session, productBase64, aspectRatio } = options
    const signal = options.abortSignal ?? this.abortController?.signal

    if (!session.selectedTemplate) {
      throw new Error('No AI model selected')
    }
    if (signal?.aborted) return this.abortedGenerationResult()

    await this.ensurePromptMetadata(session, productBase64, signal)
    if (signal?.aborted || (signal && !this.isCurrentGeneration(signal))) {
      return this.abortedGenerationResult()
    }

    const modelImageBase64 = session.selectedTemplate.imageUrl
      ? await this.resolveModelReferenceImage(session.selectedTemplate.imageUrl, signal)
      : null
    if (signal?.aborted || (signal && !this.isCurrentGeneration(signal))) {
      return this.abortedGenerationResult()
    }
    if (session.selectedTemplate.imageUrl && !modelImageBase64) {
      throw new Error('Failed to load model reference image')
    }

    const imageIndices: ImageIndices = {
      productImageCount: 1,
      productImageStartIndex: 1,
      modelImageIndex: modelImageBase64 ? 2 : null,
    }

    const structuredPrompt = buildStructuredPrompt(session.promptMetadata!, {
      poseOverride: session.poseOverride,
      compositionOverride: session.compositionOverride,
      backgroundPromptOverride: session.backgroundPromptOverride ?? 'studio background',
      bodyType: session.selectedTemplate.prompt || null,
      age: session.selectedTemplate.age ?? null,
      isWearable: session.promptMetadata!.isWearable ?? null,
      isMannequinSubject: isMannequinSubjectGender(session.selectedTemplate.gender),
    }, imageIndices)

    const snapshotPoseSelectedTileKey = session.poseSelectedTileKey
    const { attachments, renderPrompt } = this.buildGenerationInput(
      session,
      productBase64,
      modelImageBase64,
      options.poseSelectedTileImage,
      structuredPrompt
    )

    const { success, image } = await this.deps.generateImage(
      attachments,
      renderPrompt,
      ModelToRun.GEMINI_3_1,
      null,
      aspectRatio,
      null,
      signal,
      { type: 'AI_models', prompt: structuredPrompt },
      undefined,
      // iOS parity: avoid false positives on app-generated structured base prompt content.
      { skipPromptSafety: true }
    )

    session.poseSelectedTileKey = null

    if (signal?.aborted || (signal && !this.isCurrentGeneration(signal))) {
      return this.abortedGenerationResult(structuredPrompt, snapshotPoseSelectedTileKey)
    }
    if (!success || !image) {
      return {
        success: false,
        image: null,
        structuredPrompt,
        snapshotPoseSelectedTileKey,
        aborted: false,
      }
    }

    let resizedImage = this.deps.resultMaxEdge != null
      ? await this.deps.imageProcessing.resizeBase64ToMaxEdgeSize(image, this.deps.resultMaxEdge)
      : image
    if (aspectRatio > 0) {
      // Near-misses are left alone here: this flow's frame follows the result.
      resizedImage = await centerCropToAspectRatio(resizedImage, aspectRatio, AI_FASHION_CROP_TOLERANCE)
    }
    if (signal?.aborted || (signal && !this.isCurrentGeneration(signal))) {
      return this.abortedGenerationResult(structuredPrompt, snapshotPoseSelectedTileKey)
    }

    session.cachedLastGeneratedImage = resizedImage
    return {
      success: true,
      image: resizedImage,
      structuredPrompt,
      snapshotPoseSelectedTileKey,
      aborted: false,
    }
  }

  async resolveModelReferenceImage(modelUrl: string, signal?: AbortSignal): Promise<string | null> {
    if (this.cachedModelRefImage?.url === modelUrl) {
      return this.cachedModelRefImage.base64
    }
    const base64 = this.deps.fetchModelReferenceImage
      ? await this.deps.fetchModelReferenceImage(modelUrl, signal)
      : await this.fetchModelReferenceImage(modelUrl, signal)
    if (base64) {
      this.cachedModelRefImage = { url: modelUrl, base64 }
      return base64
    }
    return this.cachedModelRefImage?.base64 ?? null
  }

  private getCurrentPoseSignature(session: AIFashionSession): PoseCacheSignature {
    return getPoseSignatureFromSession(session)
  }

  private async generatePoseTiles(
    session: AIFashionSession,
    compositions: AIModelsPoseComposition[],
    productBase64: string,
    aspectRatio: number
  ): Promise<PoseTile[]> {
    const results = await this.generatePoseGrid(session, compositions, productBase64, aspectRatio)
    return results.map(r => ({
      src: r.image,
      title: r.composition.pose,
      composition: r.composition,
    }))
  }

  private async ensurePromptMetadata(
    session: AIFashionSession,
    productBase64: string,
    signal?: AbortSignal
  ): Promise<void> {
    // iOS parity: metadata no longer depends on model gender, so it is cached by product only.
    const productKey = session.productImageCacheKey ?? null
    const hasFreshMetadata = (
      session.promptMetadata !== null &&
      session.metadataProductKey === productKey
    )
    if (hasFreshMetadata) return

    const smallProduct = await this.deps.imageProcessing.resizeBase64ToMaxEdgeSizeAsJpeg(productBase64, 256, 0.8)
    const metadata = await this.deps.api.getAIModelsPromptMetadata(smallProduct, signal)
    session.promptMetadata = metadata
    session.metadataProductKey = productKey
  }

  private buildGenerationInput(
    session: AIFashionSession,
    productBase64: string,
    modelImageBase64: string | null,
    poseSelectedTileImage: string | null,
    structuredPrompt: string
  ): { attachments: string[]; renderPrompt: string } {
    if (poseSelectedTileImage && session.compositionOverride) {
      const attachments: string[] = [poseSelectedTileImage, productBase64]
      if (modelImageBase64) attachments.push(modelImageBase64)
      return { attachments, renderPrompt: HI_RES_COMPOSITION_PROMPT }
    }

    const attachments = modelImageBase64
      ? [productBase64, modelImageBase64]
      : [productBase64]
    return { attachments, renderPrompt: structuredPrompt }
  }

  private async fetchModelReferenceImage(
    imageUrl: string,
    abortSignal?: AbortSignal
  ): Promise<string | null> {
    try {
      const cacheBustUrl = imageUrl + (imageUrl.includes('?') ? '&' : '?') + '_cors=1'
      const imageLoader = this.deps.loadImageFromURL ?? loadImageFromURL
      const htmlImg = await imageLoader(cacheBustUrl) as unknown as HTMLImageElement | null
      if (!htmlImg || abortSignal?.aborted) return null
      const canvas = document.createElement('canvas')
      canvas.width = htmlImg.naturalWidth
      canvas.height = htmlImg.naturalHeight
      const ctx = canvas.getContext('2d')!
      ctx.drawImage(htmlImg, 0, 0)
      return canvas.toDataURL('image/jpeg', 0.9)
    } catch (err: any) {
      console.error('Failed to fetch model reference image:', err)
      return null
    }
  }

  private abortedGenerationResult(
    structuredPrompt: string | null = null,
    snapshotPoseSelectedTileKey: string | null = null
  ): GenerationResult {
    return {
      success: false,
      image: null,
      structuredPrompt,
      snapshotPoseSelectedTileKey,
      aborted: true,
    }
  }
}

export default AIFashionService
